import { HttpHeaders } from '@angular/common/http';
import { LANGUAGE_COOKIE, AUTH_TOKEN_KEY } from './constants';


/**
 * Ortama (hostname) göre Base API URL seçimi yapar.
 * - Tanımlı prod domainleri için 'api.dentcam.net' kullanır.
 * - Localhost veya diğer geliştirme domainleri için 'dev-api.dentcam.net' kullanır.
 */
/** 
 * Prod olarak kabul edilen ana domainler 
 */
export const PROD_HOSTNAMES = [
  'autobar.design',
  'account.autobar.design',
  'clinic.autobar.design',
  'lab.autobar.design',
  'cad.autobar.design',
  'portal.autobar.design',
  'landing.autobar.design'
];

/**
 * Mevcut hostname'in prod ortamı olup olmadığını kontrol eder
 */
export const isProdEnvironment = (): boolean => {
  if (typeof window === 'undefined') return true;
  
  const hostname = window.location.hostname.toLowerCase().startsWith('www.')
    ? window.location.hostname.toLowerCase().slice(4)
    : window.location.hostname.toLowerCase();
    
  return PROD_HOSTNAMES.includes(hostname);
};

/**
 * Mevcut uygulamanın Account uygulaması olup olmadığını kontrol eder
 */
export const isAccountApp = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  const hostname = window.location.hostname.toLowerCase();
  const port = window.location.port;
  
  // Prod & Dev domain kontrolü
  if (hostname === 'account.autobar.design' || hostname === 'dev-account.autobar.design') {
    return true;
  }
  
  // Localhost & Subdomain kontrolü
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === 'account.localhost') {
    // Account uygulaması genelde 4202 portunda çalışır
    return port === '4202' || hostname === 'account.localhost';
  }
  
  // Generic subdomain kontrolü (account.*)
  if (hostname.startsWith('account.')) {
    return true;
  }
  
  return false;
};

/**
 * Geçici: local/dev isteklerini canlı API'ye yönlendir.
 * İş bitince `false` yap.
 */
const FORCE_LIVE_API = false;

/**
 * Ortama (hostname) göre Base API URL seçimi yapar.
 */
const getBaseApiUrlByContext = (): string => {
  if (FORCE_LIVE_API) {
    return 'https://api.dentcam.net/';
  }

  if (typeof window === 'undefined') {
    return 'https://api.dentcam.net/'; // SSR için varsayılan prod API
  }

  if (isProdEnvironment()) {
    return 'https://api.dentcam.net/';
  }

  // Development ortamı (dev-api) veya Localhost
  return 'https://dev-api.dentcam.net/';
};

export const BASE_API_URL = getBaseApiUrlByContext();

/**
 * API Endpoint URL'leri
 * Tüm API servisleri için merkezi endpoint tanımlamaları
 */

export const API_ENDPOINTS = {
  // Auth endpoint'leri
  auth: {
    login: 'api/Auth/Login',
    checkActiveSession: 'api/Auth/CheckActiveSession',
    register: 'api/Auth/register',
    logout: 'api/auth/logout',
    refreshToken: 'api/Auth/RefreshToken',
    me: 'api/Auth/Me',
    forgetPassword: 'api/Auth/ForgetPassword',
    changePassword: 'api/Auth/ChangePassword',
    userActivation: 'api/Auth/UserActivation',
    resendActivationEmail: 'api/Auth/ResendActivationEmail',
  },

  // User endpoint'leri
  user: {
    getById: 'api/User/GetById',
    getAllByFilter: 'api/User/GetAllUserByFilter',
    update: 'api/User/Put',
    changePassword: 'api/User/ChangePassword',
    changeOrganization: 'api/User/ChangeOrganization',
    save: 'api/User/UserSave',
    delete: 'api/User/Delete',
  },

  // User Settings endpoint'leri
  userSettings: {
    getByUser: 'api/UserSettings/GetByUser',
    create: 'api/UserSettings/Post',
    update: 'api/UserSettings/Put',
  },

  // Chat endpoint'leri
  chat: {
    post: 'api/ChatGroupMessage/Post',
    getAll: 'api/ChatGroupMessage/GetAll',
    getByGroupName: 'api/ChatGroupMessage/GetChatGroupMessageByGroupName',
    sendMessageToGroup: 'api/ChatGroupMessage/SendMessageToGroup',
    getUserByUserName: 'api/ChatUser/GetChatUserByUserName',
    markAllRead: 'api/ChatGroupMessageAction/AllReadMessageToGroup',
  },

  // Organization endpoint'leri
  organization: {
    create: 'api/Organization/Post',
    getById: 'api/Organization/GetById',
    update: 'api/Organization/Put',
  },

  // User Organization endpoint'leri
  userOrganization: {
    getAllByUser: 'api/UserOrganization/GetAllUserOrganizationByUser',
    create: 'api/UserOrganization/Post',
    delete: 'api/UserOrganization/Delete',
  },

  // Patient endpoint'leri
  patient: {
    getAll: 'api/Patient/GetAll',
    getAllByFilter: 'api/Patient/GetAllPatientByFilter',
    getAllByGlobalSearch: 'api/Patient/GetAllPatientByGlobalSearch',
    getById: 'api/Patient/GetById',
    create: 'api/Patient/Post',
    update: 'api/Patient/Put',
    delete: 'api/Patient/Delete',
    createTokenForQrAttachment: 'api/Patient/CreateTokenForQrAttachment',
    validateTokenForQrAttachment: 'api/Patient/ValidateTokenForQrAttachment',
  },

  // Clinical Service endpoint'leri
  clinicalService: {
    getAllByFilter: 'api/ClinicalService/GetAllClinicalServiceByFilter',
    getById: 'api/ClinicalService/GetById',
    create: 'api/ClinicalService/Post',
    update: 'api/ClinicalService/Put',
    delete: 'api/ClinicalService/Delete',
  },

  // Form endpoint'leri
  form: {
    getAllByFilter: 'api/Form/GetAllFormByFilter',
    create: 'api/Form/Post',
    delete: 'api/Form/Delete',
    update: 'api/Form/Put',
  },

  // Patient Form endpoint'leri
  patientForm: {
    save: 'api/PatientForm/Save',
    delete: 'api/PatientForm/Delete',
    getAllByFilter: 'api/PatientForm/GetAllPatientFormByFilter',
    update: 'api/PatientForm/Put',
  },

  // Clinical Service Group endpoint'leri
  clinicalServiceGroup: {
    getAll: 'api/ClinicalServiceGroup/GetAll',
    getAllByFilter: 'api/ClinicalServiceGroup/GetAllClinicalServiceGroupByFilter',
    create: 'api/ClinicalServiceGroup/Post',
    update: 'api/ClinicalServiceGroup/Put',
    delete: 'api/ClinicalServiceGroup/Delete',
  },

  // Clinical Service Type endpoint'leri
  clinicalServiceType: {
    getAll: 'api/ClinicalServiceType/GetAll',
    getAllByFilter: 'api/ClinicalServiceType/GetAllClinicalServiceTypeByFilter',
    create: 'api/ClinicalServiceType/Post',
    update: 'api/ClinicalServiceType/Put',
    delete: 'api/ClinicalServiceType/Delete',
  },

  // Insurance Type endpoint'leri
  insuranceType: {
    getAllByFilter: 'api/InsuranceType/GetAllInsuranceTypeByFilter',
  },

  // Insurance Company endpoint'leri
  insuranceCompany: {
    getAllByFilter: 'api/InsuranceCompany/GetAllInsuranceCompanyByFilter',
  },

  // Package Feature endpoint'leri
  packageFeature: {
    getAllByFilter: 'api/PackageFeature/GetAllPackageFeatureByFilter',
    create: 'api/PackageFeature/Post',
    delete: 'api/PackageFeature/Delete',
  },

  // Package endpoint'leri
  package: {
    getAllPackageWithFeature: 'api/Package/GetAllPackageWithFeature',
    getAllByFilter: 'api/Package/GetAllPackageByFilter',
    create: 'api/Package/Post',
    update: 'api/Package/Put',
    delete: 'api/Package/Delete',
  },

  // Country endpoint'leri
  country: {
    getAll: 'api/Country/GetAll',
  },

  // State endpoint'leri
  state: {
    getAllStateByCountryId: 'api/State/GetAllStateByCountryId',
  },

  // City endpoint'leri
  city: {
    getAllCityByStateId: 'api/City/GetAllCityByStateId',
  },

  // User endpoint'leri (shared-domains)
  userDomain: {
    getAllByType: 'api/User/GetAllByType',
  },

  // UserBranch endpoint'leri
  userBranch: {
    getAll: 'api/UserBranch/GetAll',
    getAllByRoleIdAndFilter: 'api/UserBranch/GetAllUserBranchByRoleIdAndFilter',
    getAllUserBranchByUserWithBranchId: 'api/UserBranch/GetAllUserBranchByUserWithBranchId',
    delete: 'api/UserBranch/Delete',
  },

  // Branch endpoint'leri
  branch: {
    getAllByTypeWithFilter: 'api/Branch/GetAllBranchByTypeWithFilter',
    getAllByOrganizationWithFilter: 'api/Branch/GetAllBranchByOrganizationWithFilter',
    create: 'api/Branch/Post',
    update: 'api/branch/put',
    delete: 'api/Branch/Delete',
    BranchInvitedUser: 'api/Branch/BranchInvetedUser',
    BranchInvetedAcceptUser: 'api/Branch/BranchInvetedAcceptUser',
  },



  // Appointment endpoint'leri
  appointment: {
    getAllByFilter: 'api/Appointment/GetAllAppointmentByFilter',
    getAllByGlobalSearch: 'api/Appointment/GetAllAppointmentByGlobalSearch',
    getCountByToday: 'api/Appointment/GetCountAppointmentByToday',
    getCountPatient: 'api/Appointment/GetCountAppointmentPatient',
    create: 'api/Appointment/Post',
    update: 'api/Appointment/Put',
    delete: 'api/Appointment/Delete',
  },

  // Patient Clinical Service endpoint'leri
  patientClinicalService: {
    getAllByFilter: 'api/PatientClinicalService/GetAllPatientClinicalServiceByFilter',
    getTreatmentDistribution: 'api/PatientClinicalService/GetTreatmentDistribution',
    create: 'api/PatientClinicalService/Post',
    update: 'api/PatientClinicalService/Put',
    delete: 'api/PatientClinicalService/Delete',
  },

  // Clinical Service Price endpoint'leri
  clinicalServicePrice: {
    getAllByFilter: 'api/ClinicalServicePrice/GetAllClinicalServicePriceByFilter',
    create: 'api/ClinicalServicePrice/Post',
    update: 'api/ClinicalServicePrice/Put',
    delete: 'api/ClinicalServicePrice/Delete',
  },

  // Clinical Service Price Detail endpoint'leri
  clinicalServicePriceDetail: {
    getAllByFilter: 'api/ClinicalServicePriceDetail/GetAllClinicalServicePriceDetailByFilter',
    delete: 'api/ClinicalServicePriceDetail/Delete',
    create: 'api/ClinicalServicePriceDetail/Post',
    update: 'api/ClinicalServicePriceDetail/Put',
  },

  // Clinical Service User endpoint'leri
  clinicalServiceUser: {
    create: 'api/ClinicalServiceUser/Post',
    delete: 'api/ClinicalServiceUser/Delete',
  },

  // Attachment/File Upload endpoint'leri
  attachment: {
    getAllByStaticFile: 'api/Attachment/GetAllAttachmentByStaticFile',
    upload: 'api/Attachment/Post',
    uploadCrypto: 'api/Attachment/PostCripto',
    delete: 'api/Attachment/Delete',
    getByFileName: 'api/AttachmentAzure/GetByFileName',
    getByIsActiveAndType: 'api/Attachment/GetAttachmentByIsActiveAndType',
    getAttachmentBIsActiveAndViewerJs: 'api/Attachment/GetAttachmentBIsActiveAndViewerJs',
    getAllByPatientIdAndType: 'api/Attachment/GetAllAttachmentByPatientIdAndType',
    getAllByWorkOrderIdAndType: 'api/Attachment/GetAllAttachmentByWorkOrderIdAndType',
    addAttachmentWithQr: 'api/Attachment/AddAttachmentWithQr',
  },

  // Share endpoint'leri
  share: {
    post: 'api/Share/Post',
    put: 'api/Share/Put',
    getByLink: 'api/Share/GetByLink',
  },

  // WorkOrder endpoint'leri
  workOrder: {
    getAllByFilter: 'api/WorkOrder/GetAllWorkOrderByFilter',
    getAllByGlobalSearch: 'api/WorkOrder/GetAllWorkOrderByGlobalSearch',
    getCountPendingApprovedWorkOrder: 'api/WorkOrder/GetCountPendingApprovedWorkOrder',
    getById: 'api/WorkOrder/GetById',
    save: 'api/WorkOrder/SaveWorkOrder',
    put: 'api/WorkOrder/Put',
    delete: 'api/WorkOrder/Delete',
    createTokenForQrAttachment: 'api/WorkOrder/CreateTokenForQrAttachment',
    validateTokenForQrAttachment: 'api/WorkOrder/ValidateTokenForQrAttachment',
  },

  workOrderDetail: {
    getAllByFilter: 'api/WorkOrderDetail/GetAllWorkOrderDetailByFilter',
  },

  designProject: {
    getAllByFilter: 'api/DesignProject/GetAllDesignProjectByFilter',
    getAllByGlobalSearch: 'api/DesignProject/GetAllDesignProjectByGlobalSearch',
    getTotals: 'api/DesignProject/GetTotalsDesignProject',
    getAll: 'api/DesignProject/GetAll',
    getById: 'api/DesignProject/GetById',
    post: 'api/DesignProject/Post',
    put: 'api/DesignProject/Put',
    delete: 'api/DesignProject/Delete',
  },

  // Subscription endpoint'leri
  subscription: {
    getAll: 'api/Subscription/GetAll',
    getAllByFilter: 'api/Subscription/GetAllSubscriptionByFilter',
    subscribe: 'api/Subscription/Subscribe',
    cancelSubscription: 'api/Subscription/CancelSubscription',
    cancelScheduledChange: 'api/Subscription/CancelScheduledChange',
  },

  // Invoice endpoint'leri
  invoice: {
    getAllByFilter: 'api/Invoice/GetAllInvoiceByFilter',
  },

  // Email endpoint'leri
  email: {
    getAllByFilter: 'api/Email/GetAllEmailByFilter',
    update: 'api/Email/Put',
  },

  // Role endpoint'leri
  role: {
    getAll: 'api/Role/GetAll',
    getAllByFilter: 'api/Role/GetAllRoleByFilter',
  },

  // Role Permission endpoint'leri
  rolePermission: {
    getAllByRole: 'api/RolePermission/GetAllRolePermissionByRole',
  },

  // User Permission endpoint'leri
  userPermission: {
    getAllByUser: 'api/UserPermission/GetAllUserPermissionByUser',
  },

  // Partner Branch endpoint'leri
  partnerBranch: {
    getAllByFilter: 'api/PartnerBranch/GetAllPartnerBranchByFilter',
    branchInvitedPartner: 'api/PartnerBranch/BranchInvitedPartner',
    BranchInvitedAcceptPartner: 'api/PartnerBranch/BranchInvitedAcceptPartner',
    delete: 'api/PartnerBranch/Delete',
  },

  // Payment endpoint'leri
  payment: {
    getAllByFilter: 'api/Payment/GetAllPaymentByFilter',
    getAllByGlobalSearch: 'api/Payment/GetAllPaymentByGlobalSearch',
    getTotalMonthlyRevenue: 'api/Payment/GetTotalMonthlyRevenue',
    getTotalMonthlyRevenueTrend: 'api/Payment/GetTotalMonthlyRevenueTrend',
    create: 'api/Payment/Post',
  },

  // Payment Type endpoint'leri
  paymentType: {
    getAllByFilter: 'api/PaymentType/GetAllPaymentTypeByFilter',
  },

  // Payment Method endpoint'leri
  paymentMethod: {
    getAllByFilter: 'api/PaymentMethod/GetAllPaymentMethodByFilter',
  },

  // Private Color endpoint'leri
  privateColor: {
    getAllByFilter: 'api/PrivateColor/GetAllPrivateColorByFilter',
  },

  // Desired Material endpoint'leri
  desiredMaterial: {
    getAllByFilter: 'api/DesiredMaterial/GetAllDesiredMaterialByFilter',
  },

  // Clinical Service Favorite endpoint'leri
  clinicalServiceFavorite: {
    post: 'api/ClinicalServiceFavorite/Post',
    delete: 'api/ClinicalServiceFavorite/Delete',
    getAllByFilter: 'api/ClinicalServiceFavorite/GetAllClinicalServiceFavoriteByFilter',
  },

  // Patient Treatment endpoint'leri
  patientTreatment: {
    save: 'api/PatientTreatment/Save',
    getById: 'api/PatientTreatment/GetById',
    getAllByFilter: 'api/PatientTreatment/GetAllPatientTreatmentByFilter',
    getAllByGlobalSearch: 'api/PatientTreatment/GetAllPatientTreatmentByGlobalSearch',
    delete: 'api/PatientTreatment/Delete',
  },

  // Portal endpoint'leri
  machineType: {
    getAll: 'api/MachineType/GetAll',
    getAllByFilter: 'api/MachineType/GetAllMachineTypeByFilter',
    create: 'api/MachineType/Post',
    update: 'api/MachineType/Put',
    delete: 'api/MachineType/Delete',
  },
  brand: {
    getAll: 'api/Brand/GetAll',
    getAllByFilter: 'api/Brand/GetAllBrandByFilter',
    create: 'api/Brand/Post',
    update: 'api/Brand/Put',
    delete: 'api/Brand/Delete',
  },
  machineModel: {
    getAll: 'api/MachineModel/GetAll',
    getAllByFilter: 'api/MachineModel/GetAllMachineModelByFilter',
    create: 'api/MachineModel/Post',
    update: 'api/MachineModel/Put',
    delete: 'api/MachineModel/Delete',
  },
  category: {
    getAllByFilter: 'api/Category/GetAllCategoryByFilter',
  },
  product: {
    getAllByFilter: 'api/Product/GetAllProductByFilter',
  },
  property: {
    getAllByFilter: 'api/Property/GetAllPropertyByFilter',
  },
  permission: {
    getAllByFilter: 'api/Permission/GetAllPermissionByFilter',
  },
  feature: {
    getAll: 'api/Feature/GetAll',
    getAllByFilter: 'api/Feature/GetAllFeatureByFilter',
    create: 'api/Feature/Post',
    update: 'api/Feature/Put',
    delete: 'api/Feature/Delete',
  },
  // Log endpoint'leri
  log: {
    sendLog: 'api/Log/SendLog',
  },
  // Notification endpoint'leri
  notification: {
    getAllByFilter: 'api/Notification/GetAllNotificationByFilter',
    getCountByUser: 'api/Notification/GetCountNotificationByUser',
    read: 'api/Notification/NotificationRead',
    readAll: 'api/Notification/AllNotificationRead',
  },

  // WhatsApp endpoint'leri (backend hazır olunca kullanılacak)
  whatsapp: {
    getConfig: 'api/WhatsApp/GetConfig',
    connect: 'api/WhatsApp/Connect',
    getStatus: 'api/WhatsApp/GetStatus',
    disconnect: 'api/WhatsApp/Disconnect',
    sendTest: 'api/WhatsApp/SendTestMessage',
  },

  // WhatsApp Template endpoint'leri
  whatsappTemplate: {
    getAllByFilter: 'api/WhatsappTemplate/GetAllWhatsappTemplateByFilter',
    update: 'api/WhatsappTemplate/Put',
  },
} as const;



/**
 * Endpoint path'inden tam API URL'i oluşturur
 * @param endpointPath - Nokta ile ayrılmış endpoint path'i (örn: 'auth.login', 'user.getById')
 * @returns Tam URL string'i (örn: 'https://api.dentcam.net/api/Auth/Login')
 * 
 * @example
 * buildApiUrl('auth.login') // 'https://api.dentcam.net/api/Auth/Login' döner
 * buildApiUrl('user.getById') // 'https://api.dentcam.net/api/User/GetById' döner
 */
export function buildApiUrl(endpointPath: string): string {
  // Base URL'in sonundaki slash'ı temizle
  const currentBaseUrl = getBaseApiUrlByContext();
  const baseUrl = currentBaseUrl.endsWith('/')
    ? currentBaseUrl.slice(0, -1)
    : currentBaseUrl;

  // Endpoint path'ini noktalara göre ayır (örn: 'auth.login' -> ['auth', 'login'])
  const pathParts = endpointPath.split('.');

  // API_ENDPOINTS objesi içinde path'i takip et
  // Örnek: 'auth.login' için önce API_ENDPOINTS.auth, sonra API_ENDPOINTS.auth.login
  let endpointValue: unknown = API_ENDPOINTS;

  for (const part of pathParts) {
    // Mevcut değer bir obje mi ve içinde bu key var mı kontrol et
    if (
      endpointValue &&
      typeof endpointValue === 'object' &&
      part in endpointValue
    ) {
      endpointValue = (endpointValue as Record<string, unknown>)[part];
    } else {
      // Path bulunamadıysa hata fırlat
      throw new Error(
        `Geçersiz endpoint path: "${endpointPath}". "${part}" bulunamadı.`
      );
    }
  }

  // Endpoint değerinin string olduğunu kontrol et
  if (typeof endpointValue !== 'string') {
    throw new Error(
      `Geçersiz endpoint path: "${endpointPath}". Endpoint bir string değil.`
    );
  }

  // Endpoint path'ini temizle (başındaki slash varsa kaldır)
  const cleanEndpoint = endpointValue.startsWith('/')
    ? endpointValue.slice(1)
    : endpointValue;

  // Base URL + Endpoint'i birleştir
  return `${baseUrl}/${cleanEndpoint}`;
}


/**
 * API request'leri için header'ları oluşturur
 * @param authorized - Authorization header'ı eklenip eklenmeyeceği
 * @returns HttpHeaders objesi
 * 
 * @example
 * const headers = buildRequestHeaders(true); // Authorization header ile
 * const headers = buildRequestHeaders(false); // Sadece Content-Type ile
 */
export function buildRequestHeaders(authorized: boolean): HttpHeaders {
  // Mevcut dili al ve Culture header'ını ayarla
  const currentLanguage = getCurrentLanguage();
  const culture = currentLanguage === 'tr' ? 'tr-TR' : 'en-US';

  const headers = new HttpHeaders({
    'Content-Type': 'application/json; charset=utf-8',
    'Accept': 'application/json',
    'Culture': culture
  });

  if (!authorized) {
    return headers;
  }

  // Token'ı cookie, localStorage veya sessionStorage'dan al
  const token = getToken();

  if (token) {
    return headers.set('Authorization', `Bearer ${token}`);
  }

  return headers;
}

/**
 * HTTP request options'ını oluşturur (headers + withCredentials)
 * @param authorized - Authorization header'ı eklenip eklenmeyeceği
 * @returns HttpOptions objesi
 * 
 * @example
 * const options = buildRequestOptions(true); // Authorization header ve withCredentials ile
 * const options = buildRequestOptions(false); // Sadece Content-Type ve withCredentials ile
 */
export function buildRequestOptions(authorized: boolean): { headers: HttpHeaders; } {
  return {
    headers: buildRequestHeaders(authorized),
  };
}

/**
 * Token'ı cookie, localStorage veya sessionStorage'dan alır
 * Öncelik sırası: Cookie -> localStorage -> sessionStorage
 * @returns Token string'i veya null
 */
function getToken(): string | null {
  // SSR (Server-Side Rendering) kontrolü
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null;
  }

  // 1. Önce cookie'den kontrol et
  const cookieToken = getCookie('accessToken');
  if (cookieToken) {
    return cookieToken;
  }

  // 2. Cookie'de yoksa localStorage'dan kontrol et
  try {
    const localToken = localStorage.getItem(AUTH_TOKEN_KEY);
    if (localToken) {
      return localToken;
    }
  } catch (error) {
    // localStorage'a erişim hatası (örneğin private browsing mode)
    console.warn('Failed to access localStorage:', error);
  }

  // 3. localStorage'ta da yoksa sessionStorage'dan kontrol et
  try {
    const sessionToken = sessionStorage.getItem(AUTH_TOKEN_KEY);
    if (sessionToken) {
      return sessionToken;
    }
  } catch (error) {
    // sessionStorage'a erişim hatası
    console.warn('Failed to access sessionStorage:', error);
  }

  return null;
}

/**
 * Cookie'den değer okur
 * @param name - Cookie adı
 * @returns Cookie değeri veya null
 */
function getCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  // Cookie string'i yoksa veya boşsa null döndür
  if (!document.cookie) {
    return null;
  }

  // Cookie'leri parse et - hem '; ' hem de ';' ile ayrılmış cookie'leri destekle
  const cookies = document.cookie.split(/;\s*/);

  for (const cookie of cookies) {
    const trimmedCookie = cookie.trim();
    if (trimmedCookie.startsWith(`${name}=`)) {
      const value = trimmedCookie.substring(name.length + 1);
      try {
        return decodeURIComponent(value);
      } catch (error) {
        // decodeURIComponent hatası durumunda raw value'yu döndür
        console.warn(`Failed to decode cookie value for ${name}:`, error);
        return value;
      }
    }
  }

  return null;
}

/**
 * Mevcut uygulama dilini alır (cookie, localStorage veya varsayılan olarak 'en')
 * @returns Dil kodu ('tr' veya 'en')
 */
function getCurrentLanguage(): string {
  // Önce cookie'den kontrol et
  const cookieLanguage = getCookie(LANGUAGE_COOKIE);
  if (cookieLanguage) {
    return cookieLanguage.toLowerCase();
  }

  // Sonra localStorage'dan kontrol et
  if (typeof localStorage !== 'undefined') {
    const localLanguage = localStorage.getItem('language');
    if (localLanguage) {
      return localLanguage.toLowerCase();
    }
  }

  // Varsayılan olarak 'en' döndür
  return 'en';
}

